import { useEffect, useState } from 'react';
import { Popover, Box, Icon, Button } from '@nimbus-ds/components';
import { EllipsisIcon, PrinterIcon } from '@nimbus-ds/icons';
import { Menu, MenuButton } from '@nimbus-ds/patterns';
import { InterfaceButton } from '@tiendanube/components';
import BackNavigation from 'App/components/lab/BackNavigation';
import useTopBar from 'App/components/Topbar/useTopBar';
import { ActionProp } from 'commons/components';
import { useResponsive } from 'commons/components/Responsive';
import { useTranslationCommon } from 'commons/hooks';
import { useHasUnifiedShippingDocumentsEnabled } from 'domains/Auth/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

export interface HeaderOrderMobileProps {
  navigation?: Pick<InterfaceButton, 'children' | 'onClick'>;
  actions: ActionProp[];
  mainAction?: ActionProp;
  applications?: ActionProp[];
  showAllways?: boolean;
}

function HeaderOrderMobile({
  navigation,
  actions,
  applications,
  mainAction,
  showAllways = false,
}: Readonly<HeaderOrderMobileProps>) {
  const { isMobile } = useResponsive();
  const { setHeaderNavigation } = useTopBar();
  const [isVisiblePopoverOptions, setIsVisiblePopoverOptions] = useState(false);
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();

  useEffect(() => {
    !showAllways && setHeaderNavigation(navigation);
  }, [navigation, setHeaderNavigation, showAllways]);

  const tOrd = useTranslationOrders();
  const tComm = useTranslationCommon();

  if (!(isMobile || showAllways)) return null;

  const printButtonText = isUnifiedShippingDocumentsEnabled
    ? tOrd('detail.printShippingDocuments')
    : tOrd('detail.printLabels');

  const printButtonSingleText = isUnifiedShippingDocumentsEnabled
    ? tOrd('detail.printShippingDocument')
    : tOrd('detail.printLabel');

  const handleOnOptionClick = (action: ActionProp) => {
    action.onClick?.();
    setIsVisiblePopoverOptions(false);
  };

  const renderBackNavigation = navigation && (
    <BackNavigation onClick={navigation.onClick}>
      {navigation.children}
    </BackNavigation>
  );

  const printLabelActions = actions.filter(
    (action) => action.name && action.name === 'printLabel',
  );

  const otherActions = actions.filter(
    (action) => !action.name || action.name !== 'printLabel',
  );
  const buttomActions = otherActions && (
    <Popover
      key="actionsOrder"
      arrow={false}
      position="bottom"
      onVisibility={(visible) => setIsVisiblePopoverOptions(visible)}
      visible={isVisiblePopoverOptions}
      content={
        <Box flexDirection="column">
          <Box padding="1" flexDirection="column">
            {otherActions.map((action) => (
              <MenuButton
                key={`action-${action.children}`}
                label={action.children ?? 'undefined'}
                startIcon={action.icon}
                onClick={() => handleOnOptionClick(action)}
              />
            ))}
          </Box>
          {applications && applications.length > 0 && (
            <Menu.Section title={tComm('myApps')}>
              <Box flexDirection="column" padding="1">
                {applications.map((application) => (
                  <MenuButton
                    key={`application-${application.children}`}
                    label={application.children ?? 'undefined'}
                    startIcon={application.icon}
                    onClick={() => handleOnOptionClick(application)}
                  />
                ))}
              </Box>
            </Menu.Section>
          )}
        </Box>
      }
    >
      <Icon source={<EllipsisIcon />} color="neutral-textHigh" />
    </Popover>
  );

  const printActions =
    printLabelActions && printLabelActions.length > 1 ? (
      <Popover
        key="printLabelOrder"
        arrow={false}
        position="bottom-end"
        content={
          <Box flexDirection="column">
            {printLabelActions.map((printLabelAction) => (
              <Box
                key={`printAction-${printLabelAction.children}`}
                padding="1"
                flexDirection="column"
              >
                <MenuButton
                  label={printLabelAction.children ?? 'lala'}
                  startIcon={printLabelAction.icon}
                  onClick={() => handleOnOptionClick(printLabelAction)}
                />
              </Box>
            ))}
          </Box>
        }
      >
        <Button key={`printLabels-${0}`} appearance="transparent">
          <Icon source={<PrinterIcon />} color="neutral-textHigh" />
          {printButtonText}
        </Button>
      </Popover>
    ) : (
      printLabelActions.length === 1 && (
        <Button
          key={`printLabel-${0}`}
          onClick={printLabelActions[0].onClick}
          appearance="transparent"
        >
          <Icon source={<PrinterIcon />} color="neutral-textHigh" />
          {printButtonSingleText}
        </Button>
      )
    );

  const renderMainAction = mainAction && mainAction !== undefined && (
    <Button
      key="mainAction-0"
      onClick={mainAction.onClick}
      appearance="transparent"
      disabled={mainAction.disabled}
    >
      {mainAction.children}
    </Button>
  );

  return (
    <Box display="flex" justifyContent="flex-end" padding="2">
      {renderBackNavigation}
      <Box
        alignItems="center"
        display="flex"
        flexWrap="nowrap"
        justifyContent="flex-end"
        textAlign="right"
      >
        {renderMainAction}
        {printActions}
        {buttomActions}
      </Box>
    </Box>
  );
}

export default HeaderOrderMobile;
